// (c) chaehongee. Confidential and proprietary.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface Options {
    defaultWidthPx?: number
    minThreadPx?: number
    minCanvasPx?: number
    storageKey?: string
}

interface ResizerApi {
    threadWidth: number
    isDragging: boolean
    isCollapsed: boolean
    cssVars: Record<string, string>
    handlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void
    handleDoubleClick: () => void
}

export function useThreadCanvasResizer(
    rootRef: React.RefObject<HTMLDivElement | null>,
    options: Options = {},
): ResizerApi {
    // Panel geometry in px. defaultWidthPx (384) mirrors the CSS --thread-width
    // fallback (24rem) in Layout.module.css; keep the two in sync. min/collapsed
    // widths have no CSS token counterpart and live here only.
    const {
        defaultWidthPx = 384,
        minThreadPx = 240,
        minCanvasPx = 480,
        storageKey = 'knoll.threadWidthPx',
    } = options

    const COLLAPSED_WIDTH = 56
    const [isDragging, setIsDragging] = useState<boolean>(false)
    const [threadWidth, setThreadWidth] = useState<number>(() => {
        const saved = Number(window.localStorage.getItem(storageKey) || '0')
        return Number.isFinite(saved) && saved > 0 ? saved : defaultWidthPx
    })
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        return window.localStorage.getItem('knoll.threadCollapsed') === '1'
    })
    const threadWidthRef = useRef<number>(threadWidth)
    useEffect(() => {
        threadWidthRef.current = threadWidth
    }, [threadWidth])

    const cssVars = useMemo(() => {
        return { ['--thread-width' as unknown as string]: `${threadWidth}px` }
    }, [threadWidth])

    const dispatchPanelResize = useCallback((widthPx: number) => {
        window.dispatchEvent(new CustomEvent('panelresize', { detail: { threadWidth: widthPx } }))
    }, [])

    const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(true)
        const rootEl = rootRef.current
        if (!rootEl) return
        const rect = rootEl.getBoundingClientRect()
        const maxThread = Math.max(minThreadPx, rect.width - minCanvasPx)

        const onMove = (ev: PointerEvent) => {
            const x = ev.clientX
            let next = x - rect.left
            if (Number.isNaN(next)) return
            next = Math.min(Math.max(next, minThreadPx), maxThread)
            if (next !== threadWidthRef.current) {
                threadWidthRef.current = next
                setThreadWidth(next)
                dispatchPanelResize(next)
            }
        }
        const onUp = () => {
            setIsDragging(false)
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            window.localStorage.setItem(storageKey, String(threadWidthRef.current))
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp, { once: true })
    }, [dispatchPanelResize, minCanvasPx, minThreadPx, rootRef, storageKey])

    const handleDoubleClick = useCallback(() => {
        setThreadWidth(defaultWidthPx)
        threadWidthRef.current = defaultWidthPx
        window.localStorage.setItem(storageKey, String(defaultWidthPx))
        dispatchPanelResize(defaultWidthPx)
    }, [defaultWidthPx, dispatchPanelResize, storageKey])

    // Initialize collapsed width if persisted
    useEffect(() => {
        if (isCollapsed) {
            setThreadWidth(COLLAPSED_WIDTH)
            threadWidthRef.current = COLLAPSED_WIDTH
            dispatchPanelResize(COLLAPSED_WIDTH)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Listen for collapse requests from thread UI
    useEffect(() => {
        const onCollapse = (e: Event) => {
            const evt = e as CustomEvent<{ collapsed: boolean } | undefined>
            const collapsed = Boolean(evt.detail?.collapsed)
            setIsCollapsed(collapsed)
            window.localStorage.setItem('knoll.threadCollapsed', collapsed ? '1' : '0')
            if (collapsed) {
                // save last expanded width
                window.localStorage.setItem('knoll.threadWidthPx:last', String(threadWidthRef.current))
                setThreadWidth(COLLAPSED_WIDTH)
                threadWidthRef.current = COLLAPSED_WIDTH
                dispatchPanelResize(COLLAPSED_WIDTH)
            } else {
                const last = Number(window.localStorage.getItem('knoll.threadWidthPx:last') || '0')
                const next = Number.isFinite(last) && last >= minThreadPx ? last : defaultWidthPx
                setThreadWidth(next)
                threadWidthRef.current = next
                window.localStorage.setItem(storageKey, String(next))
                dispatchPanelResize(next)
            }
        }
        window.addEventListener('thread:collapse', onCollapse as EventListener)
        return () => window.removeEventListener('thread:collapse', onCollapse as EventListener)
    }, [COLLAPSED_WIDTH, defaultWidthPx, dispatchPanelResize, minThreadPx, storageKey])

    return {
        threadWidth,
        isDragging,
        isCollapsed,
        cssVars,
        handlePointerDown,
        handleDoubleClick,
    }
}


